import { useEffect, useMemo } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { buildErrorReport } from '@/lib/buildErrorReport';
import { reportClientError, newTraceId } from '@/api/clientErrors';
import { NoticeScreen } from './NoticeScreen';
import { FailureScreen } from './FailureScreen';
import { offersDashboardExit } from './failureScreenExits';
import { testIds } from '@/test/testIds';
import { paths } from '@/constants';

/**
 * A "Failure" is the unknown-cause class — a thrown Error, a 5xx route
 * response, or anything else thrown. 4xx route responses (404/403/...) are
 * "Notices": a known cause and a meaningful next step, not a broken state.
 */
function isFailure(error: unknown): boolean {
  if (isRouteErrorResponse(error)) return error.status >= 500;
  return true;
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const status = isRouteErrorResponse(error) ? error.status : undefined;

  // One trace id per Failure, shared by the telemetry report and the
  // FailureScreen. Keyed on `error` so each distinct failure gets a fresh id.
  const traceId = useMemo(
    () => (isFailure(error) ? newTraceId() : undefined),
    [error],
  );

  // Escalate Failures to telemetry. The Effect keys on `error`, so each
  // distinct failure reports exactly once.
  useEffect(() => {
    if (!isFailure(error)) return;
    reportClientError({
      error: error instanceof Error ? error : new Error(`Route failure ${status ?? ''}`.trim()),
      source: 'boundary',
      traceId,
    });
  }, [error, status, traceId]);

  if (isFailure(error)) {
    const report = buildErrorReport({
      title: status ? `Server Error (${status})` : 'Application Error',
      message: error instanceof Error
        ? error.message
        : status ? `Server responded ${status}` : 'Unknown error',
      source: 'route',
      status,
      error: error instanceof Error ? error : undefined,
      traceId,
    });
    return (
      <FailureScreen
        traceId={traceId ?? ''}
        report={report}
        onTryAgain={() => window.location.reload()}
        onDashboard={offersDashboardExit() ? () => navigate(paths.root) : undefined}
      />
    );
  }

  // --- Notice cases: 4xx route responses. A known outcome, not a crash —
  // no trace id, no telemetry, no debug panel.
  let title = 'Page unavailable';
  let message = 'This page can’t be shown right now.';
  let icon = '?';
  let iconBgColor = '#fef9ec';

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found';
      message = "We couldn't find what you're looking for. It may have been moved or removed.";
    } else if (error.status === 403) {
      title = 'Access restricted';
      message = "You don't have permission to view this page. Contact your administrator if you think this is a mistake.";
      icon = '–';
      iconBgColor = '#fef2f2';
    }
  }

  return (
    <NoticeScreen
      title={title}
      message={message}
      icon={icon}
      iconBgColor={iconBgColor}
      secondaryAction={{ label: 'Go back', onClick: () => navigate(-1) }}
      primaryAction={{ label: 'Dashboard', onClick: () => navigate(paths.root) }}
      testId={testIds.components.routeErrorBoundary.icon}
    />
  );
}
